import React, { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { QtyStepper, WeightNote } from "@/components/QtyStepper";
import { unitWeightKg } from "@/lib/weight";
import { useAuth } from "@/context/AuthContext";

const LABEL = {
  catalog_product: ["Catalog product", "bg-secondary text-ink"],
  available: ["Available", "bg-emerald/10 text-emerald-deep"],
  price_verified: ["Price verified", "bg-emerald/10 text-emerald-deep"],
  price_on_request: ["Price on request", "bg-coral/10 text-coral"],
  out_of_stock: ["Out of stock", "bg-black/10 text-ink"],
  availability_unknown: ["Availability unknown", "bg-amber-100 text-amber-800"],
};

export function AvailabilityBadge({ status, className = "" }) {
  const [t, c] = LABEL[status] || LABEL.availability_unknown;
  return <span data-testid={`availability-${status || "unknown"}`} className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${c} ${className}`}>{t}</span>;
}

export function PriceRequestForm({ product }) {
  const { user } = useAuth();
  const [qty, setQty] = useState(() => { const n = Number(new URLSearchParams(window.location.search).get("qty")); return n >= 1 && n <= 99 ? Math.trunc(n) : 1; });
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/catalog/price-request", { product_id: product.id, quantity: Number(qty) || 1, note, contact_email: user ? undefined : email });
      setDone(data); toast.success(`Request ${data.ref} sent to the SHOP NOW quote desk`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); } finally { setBusy(false); }
  };
  if (done) return <div className="mt-4 rounded-2xl border border-emerald/30 bg-emerald/5 p-4 text-sm" data-testid="price-request-done"><b>{done.ref}</b> · {done.message}</div>;
  return (
    <div className="mt-4 rounded-2xl border border-border p-4" data-testid="price-request-form">
      <p className="text-sm font-semibold">Order this item — we confirm the price for you</p>
      <p className="text-xs text-muted-foreground mt-0.5">Tell us how many you need. The SHOP NOW quote desk confirms the supplier price and emails you a quotation to accept.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <div className="flex flex-col gap-1"><QtyStepper value={qty} onChange={setQty} id="price-request-qty" unit={product.unit} /><WeightNote unitKg={unitWeightKg(product)} qty={qty} unit={product.unit} id="price-request-weight" compact /></div>
        {!user && <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="price-request-email" className="flex-1 min-w-[180px] rounded-xl border border-border px-3 py-2 text-sm" placeholder="Your email for the quote" />}
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="price-request-note" className="flex-1 min-w-[180px] rounded-xl border border-border px-3 py-2 text-sm" placeholder="Delivery parish / notes (optional)" />
        <button onClick={submit} disabled={busy || (!user && !email) || qty < 1} data-testid="price-request-submit" className="inline-flex items-center gap-2 rounded-xl bg-ink text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Request price & order</button>
      </div>
    </div>
  );
}
